import React, { useEffect, useRef, useMemo, useState, useCallback } from 'react'
import { PieChart, Pie, Cell, ResponsiveContainer } from 'recharts'

/* ── helpers ── */
function initials(name) {
  return (name || 'AG').split(' ').filter(Boolean).slice(0, 2).map(p => p[0]).join('').toUpperCase()
}
function fmtHMS(secs) {
  const h = Math.floor(secs / 3600), m = Math.floor((secs % 3600) / 60), s = secs % 60
  return `${String(h).padStart(2,'0')}:${String(m).padStart(2,'0')}:${String(s).padStart(2,'0')}`
}
function colorForStatus(s) {
  const v = String(s || '').toLowerCase()
  if (['ready','available','online'].includes(v)) return '#27AE60'
  if (v === 'busy') return '#E53935'
  if (v === 'short_break') return '#0EA5E9'
  if (v === 'lunch') return '#F59E0B'
  if (v === 'training_session') return '#6366F1'
  if (v === 'quality_feedback') return '#8B5CF6'
  if (v === 'offline') return '#9AA5B4'
  return '#0090D9'
}

const STATUS_OPTIONS = [
  { key: 'ready',            label: 'Ready',            sub: 'Take calls',   color: '#27AE60' },
  { key: 'busy',             label: 'Busy',             sub: 'On call',      color: '#E53935' },
  { key: 'short_break',      label: 'Short Break',      sub: 'Pause',        color: '#0EA5E9' },
  { key: 'lunch',            label: 'Lunch',            sub: 'Pause',        color: '#F59E0B' },
  { key: 'training_session', label: 'Training Session',  sub: 'Session',      color: '#6366F1' },
  { key: 'quality_feedback', label: 'Quality Feedback', sub: 'Pause',        color: '#8B5CF6' },
  { key: 'offline',          label: 'Offline',          sub: 'End shift',    color: '#9AA5B4' },
]

/* ── KPI accent colours matching HTML ── */
const KPI_COLORS = ['#27AE60', '#7C3AED', '#0090D9', '#27AE60', '#E6A817', '#E53935']


const AgentWorkspacePage = ({ currentUser, fetchJson, setCurrentPage, setSelectedTicket, dateFilter, agentStatus: agentStatusProp, setAgentStatus: setAgentStatusProp, ozonetelLoggedIn }) => {
  const [profile, setProfile]         = useState(null)
  const [kpiCard, setKpiCard]         = useState(null)
  const [topAgents, setTopAgents]     = useState([])
  const [shiftStatus, setShiftStatus]       = useState(null)
  const [shiftBreakdown, setShiftBreakdown] = useState([])
  const [loading, setLoading]         = useState(true)

  const [localAgentStatus, setLocalAgentStatus] = useState('ready')
  const agentStatus    = agentStatusProp    !== undefined ? agentStatusProp    : localAgentStatus
  const setAgentStatus = setAgentStatusProp !== undefined ? setAgentStatusProp : setLocalAgentStatus
  const [statusSecs, setStatusSecs]   = useState(0)
  const [stMenuOpen, setStMenuOpen]   = useState(false)
  const [statusError, setStatusError] = useState('')
  const statusErrorTimer = useRef(null)
  const stMenuRef = useRef(null)

  const showStatusError = (msg) => {
    clearTimeout(statusErrorTimer.current)
    setStatusError(msg)
    statusErrorTimer.current = setTimeout(() => setStatusError(''), 3000)
  }

  const [myTicketsOpen, setMyTicketsOpen]   = useState(false)
  const [myTickets, setMyTickets]           = useState([])
  const [myTicketsLoading, setMyTicketsLoading] = useState(false)

  /* fetch my tickets when panel opens */
  useEffect(() => {
    if (!myTicketsOpen || !fetchJson) return
    setMyTicketsLoading(true)
    fetchJson('/api/tickets/ticket/')
      .then(data => setMyTickets(Array.isArray(data) ? data : (data?.results || [])))
      .catch(() => setMyTickets([]))
      .finally(() => setMyTicketsLoading(false))
  }, [myTicketsOpen, fetchJson])

  /* close status menu on outside click */
  useEffect(() => {
    function h(e) { if (stMenuRef.current && !stMenuRef.current.contains(e.target)) setStMenuOpen(false) }
    document.addEventListener('mousedown', h)
    return () => document.removeEventListener('mousedown', h)
  }, [])

  /* seed the displayed seconds from the API, then kick off the interval */
  const refreshTimer = useCallback(() => {
    if (!fetchJson) return
    setStatusSecs(0)
    fetchJson('/api/agents/agent-state/status-time/')
      .then(res => setStatusSecs(Number(res?.seconds ?? 0)))
      .catch(() => {})
  }, [fetchJson])

  /* start/restart the 1-second tick whenever status changes */
  useEffect(() => {
    refreshTimer()
    const id = setInterval(() => setStatusSecs(s => s + 1), 1000)
    return () => clearInterval(id)
  }, [agentStatus, refreshTimer])

  /* fetch all workspace data */
  useEffect(() => {
    if (!fetchJson) return
    let active = true
    setLoading(true)

    const params = new URLSearchParams()
    if (dateFilter?.fromDate) params.set('from_date', dateFilter.fromDate)
    if (dateFilter?.toDate)   params.set('to_date', dateFilter.toDate)
    const kpiUrl = `/api/agents/agent/kpi-card${params.toString() ? '?' + params.toString() : ''}`

    Promise.all([
      fetchJson('/api/agents/agent/get-profile/'),
      fetchJson(kpiUrl),
      fetchJson('/api/agents/agent/top-agents/'),
      fetchJson('/api/agents/agent/shift-status/'),
      fetchJson('/api/agents/agent/shift-breakdown/'),
    ]).then(([prof, kpi, agents, shift, breakdown]) => {
      if (!active) return
      setProfile(prof)
      setKpiCard(kpi)
      setTopAgents(agents?.agents || [])
      setShiftStatus(shift)
      setShiftBreakdown(Array.isArray(breakdown) ? breakdown : [])
      if (prof?.status) setAgentStatus(String(prof.status).toLowerCase())
    }).catch(() => {}).finally(() => { if (active) setLoading(false) })

    return () => { active = false }
  }, [fetchJson, dateFilter])

  const displayName = profile?.agent_name || currentUser?.full_name || 'Agent'
  const stColor     = colorForStatus(agentStatus)
  const stOption    = STATUS_OPTIONS.find(o => o.key === agentStatus) || STATUS_OPTIONS[0]

  const profileMeta = [
    profile?.agent_code ? `ID: ${profile.agent_code}` : null,
    profile?.stage ? String(profile.stage).toUpperCase() + ' Agent' : null,
    profile?.area || currentUser?.primary_city_name || null,
    profile?.shift ? `Shift: ${profile.shift}` : null,
  ].filter(Boolean).join(' · ')

  /* KPI cards */
  const kpis = [
    {
      label: 'Total Calls', color: KPI_COLORS[0],
      value: kpiCard?.total_call ?? '—',
      sub: `Inbound ${kpiCard?.inbound_calls ?? 0} · Outbound ${kpiCard?.outbound_calls ?? 0}`,
    },
    {
      label: 'Total Chats', color: KPI_COLORS[1],
      value: kpiCard?.total_chats ?? '—',
      sub: 'Inbound & outbound',
    },
    {
      label: 'Avg Handle Time', color: KPI_COLORS[2],
      value: kpiCard?.average_handle_time || '—',
      sub: `ASA: ${kpiCard?.asa ?? '—'} · ATT: ${kpiCard?.att ?? '—'}`,
    },
    {
      label: 'CSAT Score', color: KPI_COLORS[3],
      value: kpiCard?.csat_score ? `${kpiCard.csat_score}/5` : '—',
      sub: kpiCard?.csat_pct ? `${kpiCard.csat_pct}% positive` : 'This shift',
    },
    {
      label: 'Tickets Created', color: KPI_COLORS[4],
      value: kpiCard?.tickets_created ?? '—',
      sub: `Open ${kpiCard?.open ?? 0} · In Progress ${kpiCard?.in_progress ?? 0} · Closed ${kpiCard?.closed ?? 0}`,
    },
  ]

  /* Interactions donut */
  const connected  = Number(shiftStatus?.interaction_status?.connected  ?? 0)
  const failed     = Number(shiftStatus?.interaction_status?.failed     ?? 0)
  const abandoned  = Number(shiftStatus?.interaction_status?.abandoned  ?? 0)
  const totalIntr  = connected + failed + abandoned || 1
  const donutData  = [
    { name: 'Connected', value: connected, color: '#27AE60' },
    { name: 'Failed / NA', value: failed,  color: '#E53935' },
    { name: 'Abandoned',  value: abandoned, color: '#F59E0B' },
  ]

  /* Shift breakdown list */
  const BREAKDOWN_LABELS = {
    lunch: 'Lunch', offline: 'Offline', on_call: 'On Call',
    quality_feedback: 'Quality Feedback', ready: 'Ready',
    short_break: 'Short Break', training_session: 'Training Session',
  }
  const BREAKDOWN_COLORS = {
    lunch: '#F59E0B', offline: '#9AA5B4', on_call: '#56A0D3',
    quality_feedback: '#8B5CF6', ready: '#27AE60',
    short_break: '#0EA5E9', training_session: '#6366F1',
  }
  const breakdownArr = shiftBreakdown
  const shiftTotalSecs = breakdownArr.reduce((sum, item) => sum + (item.minutes || 0) * 60, 0)
  const shiftTotal = fmtHMS(shiftTotalSecs)

  /* Shift hours progress bars */
  const shiftTargetSecs = Number(shiftStatus?.shift_hours?.target_secs ?? 32400) // 9h default
  const breakTargetSecs = Number(shiftStatus?.shift_hours?.break_target_secs ?? 3600)

  const getMins = (key) => (breakdownArr.find(i => i.status === key)?.minutes ?? 0)
  const breakSecs = (getMins('lunch') + getMins('short_break')) * 60
  const workSecs  = (getMins('ready') + getMins('on_call') + getMins('quality_feedback') + getMins('training_session')) * 60
  const shiftHoursSecs = breakSecs + workSecs

  const shiftPct = Math.min(100, Math.round(shiftHoursSecs / shiftTargetSecs * 100))
  const breakPct = Math.min(100, Math.round(breakSecs / breakTargetSecs * 100))
  const workPct  = Math.min(100, Math.round(workSecs / Math.max(shiftTargetSecs - breakTargetSecs, 1) * 100))

  /* ─── render ─── */
  return (
    <div style={{ fontFamily: "'Source Sans 3','Segoe UI',Helvetica,Arial,sans-serif", fontSize: '14px', color: '#2B2B2B' }}>

      {/* Page header */}
      <div style={{ marginBottom: '14px' }}>
        <p style={{ fontSize: '12px', color: '#5A6672', margin: 0 }}>Make calls, manage availability, and track your shift performance</p>
      </div>

      {/* Action buttons */}
      <div style={{ display: 'flex', gap: '8px', marginBottom: '14px' }}>
        <button onClick={() => setMyTicketsOpen(o => !o)} style={{
          display: 'flex', alignItems: 'center', gap: '6px', padding: '6px 14px',
          background: '#fff', border: '1px solid #E0E7EF', borderRadius: '8px',
          fontSize: '12px', fontWeight: 500, color: '#5A6672', cursor: 'pointer',
        }}>
          <svg width="14" height="14" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.6"><path d="M4 4h12a1 1 0 011 1v11a1 1 0 01-1 1H4a1 1 0 01-1-1V5a1 1 0 011-1z"/><path d="M7 9h6M7 13h4"/></svg>
          My Tickets
        </button>
      </div>

      {/* My tickets panel */}
      {myTicketsOpen && (
        <div style={{ background: '#fff', border: '1px solid #E0E7EF', borderRadius: '8px', padding: '14px', marginBottom: '14px' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <h3 style={{ fontSize: '14px', fontWeight: 600, color: '#2B2B2B', margin: 0 }}>My Tickets</h3>
              {!myTicketsLoading && (
                <span style={{ fontSize: '11px', fontWeight: 600, color: '#0090D9', background: '#E5F4FB', borderRadius: '20px', padding: '1px 8px' }}>
                  {myTickets.length}
                </span>
              )}
            </div>
            <button onClick={() => setMyTicketsOpen(false)} style={{ background: 'none', border: '1px solid #E0E7EF', borderRadius: '4px', padding: '3px 9px', fontSize: '12px', cursor: 'pointer', color: '#5A6672' }}>✕ Close</button>
          </div>

          {myTicketsLoading ? (
            <div style={{ fontSize: '12px', color: '#9AA5B1', padding: '8px 0' }}>Loading…</div>
          ) : myTickets.length === 0 ? (
            <div style={{ fontSize: '12px', color: '#9AA5B1', padding: '8px 0' }}>No tickets found.</div>
          ) : (
            <>
              {myTickets.slice(0, 3).map((t, i) => {
                const sc = t.status === 'in_progress' ? '#F59E0B' : t.status === 'resolved' ? '#27AE60' : t.status === 'closed' ? '#94A3B8' : '#3B82F6'
                const sl = t.status === 'in_progress' ? 'In Progress' : t.status === 'resolved' ? 'Resolved' : t.status === 'closed' ? 'Closed' : t.status || '—'
                return (
                  <div key={i} onClick={() => { setSelectedTicket(t); setCurrentPage('ticket-detail') }} style={{
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                    padding: '9px 0', borderBottom: '1px solid #F1F5F9', cursor: 'pointer',
                  }}>
                    <div>
                      <div style={{ fontSize: '12px', fontWeight: 600, color: '#1B4FD8', fontFamily: "'Courier New',monospace" }}>{t.ticket_number}</div>
                      <div style={{ fontSize: '11px', color: '#5A6672', marginTop: '1px' }}>
                        {[t.customer_name, t.issue_category].filter(Boolean).join(' · ') || '—'}
                      </div>
                    </div>
                    <span style={{ fontSize: '11px', fontWeight: 600, color: sc, background: `${sc}18`, borderRadius: '4px', padding: '2px 8px', whiteSpace: 'nowrap' }}>{sl}</span>
                  </div>
                )
              })}
              <button
                onClick={() => { setMyTicketsOpen(false); setCurrentPage('tickets') }}
                style={{
                  marginTop: '10px', width: '100%', padding: '8px', background: '#F8FAFB',
                  border: '1px solid #E0E7EF', borderRadius: '6px', fontSize: '12px',
                  fontWeight: 600, color: '#0090D9', cursor: 'pointer',
                }}
              >
                See all {myTickets.length} tickets in Ticket 360 →
              </button>
            </>
          )}
        </div>
      )}

      {/* HERO banner */}
      <div style={{
        display: 'grid', gridTemplateColumns: 'auto 1fr auto', gap: '16px', alignItems: 'center',
        background: 'linear-gradient(135deg, #1B6BAE 0%, #3DA0DC 100%)',
        borderRadius: '10px', padding: '16px 18px', marginBottom: '14px', color: '#fff',
      }}>
        {/* Avatar */}
        <div style={{
          width: '56px', height: '56px', borderRadius: '50%', background: '#fff',
          color: '#0090D9', display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontSize: '18px', fontWeight: 700, flexShrink: 0, position: 'relative',
        }}>
          {initials(displayName)}
          <div style={{
            position: 'absolute', bottom: '2px', right: '2px', width: '13px', height: '13px',
            borderRadius: '50%', background: stColor, border: '2px solid #56A0D3',
          }} />
        </div>

        {/* Name + meta */}
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', fontSize: '16px', fontWeight: 700 }}>
            {displayName}
            <span style={{
              display: 'inline-flex', alignItems: 'center', gap: '5px', fontSize: '10px',
              fontWeight: 500, padding: '2px 8px', borderRadius: '10px',
              background: `${stColor}30`, color: '#fff', border: `1px solid ${stColor}80`,
            }}>
              <span style={{ width: '6px', height: '6px', borderRadius: '50%', background: stColor, display: 'inline-block' }} />
              {stOption.label}
            </span>
          </div>
          <div style={{ fontSize: '11px', color: '#C8D8F0', marginTop: '2px', fontFamily: "'Courier New',monospace" }}>
            {loading ? 'Loading profile…' : profileMeta || 'Agent'}
          </div>
        </div>

        {/* Timer + status picker */}
        <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
          <div style={{ background: 'rgba(0,0,0,.2)', borderRadius: '7px', padding: '6px 14px', textAlign: 'center' }}>
            <div style={{ fontSize: '9px', fontWeight: 500, color: '#C8D8F0', textTransform: 'uppercase', letterSpacing: '.5px', marginBottom: '1px' }}>
              {stOption.label}
            </div>
            <div style={{ fontSize: '22px', fontWeight: 700, fontFamily: "'Courier New',monospace", color: '#fff', letterSpacing: '1px' }}>
              {fmtHMS(statusSecs)}
            </div>
          </div>

          <div ref={stMenuRef} style={{ position: 'relative' }}>
            <button
              onClick={() => setStMenuOpen(o => !o)}
              style={{
                display: 'flex', alignItems: 'center', gap: '8px', padding: '8px 14px',
                background: 'rgba(255,255,255,.15)', border: '1px solid rgba(255,255,255,.3)',
                borderRadius: '8px', color: '#fff', cursor: 'pointer', fontSize: '13px', fontWeight: 500,
              }}
            >
              <span style={{ width: '9px', height: '9px', borderRadius: '50%', background: stColor, display: 'inline-block' }} />
              {stOption.label}
              <svg width="11" height="11" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="2.2"><path d="M5 8l5 5 5-5"/></svg>
            </button>

            {stMenuOpen && (
              <div style={{
                position: 'absolute', top: 'calc(100% + 6px)', right: 0, minWidth: '210px',
                background: '#fff', border: '1px solid #E0E7EF', borderRadius: '8px',
                zIndex: 50, padding: '6px 0', color: '#2B2B2B', boxShadow: '0 4px 12px rgba(0,0,0,.12)',
              }}>
                <div style={{ fontSize: '10px', fontWeight: 600, color: '#5A6672', textTransform: 'uppercase', letterSpacing: '.5px', padding: '7px 12px 5px', borderBottom: '1px solid #E0E7EF', marginBottom: '4px' }}>
                  Change Status
                </div>
                {STATUS_OPTIONS.map(opt => (
                  <div key={opt.key}
                    onClick={() => {
                      if (opt.key === agentStatus) {
                        setStMenuOpen(false)
                        return
                      }
                      if (opt.key === 'busy') {
                        showStatusError("You can't change to Busy — you are not on a call.")
                        return
                      }
                      if (opt.key !== 'offline' && !ozonetelLoggedIn) {
                        showStatusError("Please log in to the dialer first.")
                        return
                      }
                      setAgentStatus(opt.key)
                      setStMenuOpen(false)
                      if (fetchJson) {
                        fetchJson('/api/agents/agent-state/change-agent-state/?action=agent', {
                          method: 'POST',
                          headers: { 'Content-Type': 'application/json' },
                          body: JSON.stringify({ state: opt.key }),
                        }).then(() => refreshTimer())
                          .catch(err => {
                          setAgentStatus(agentStatus) // revert optimistic update
                          showStatusError(err?.message || err?.detail || 'Failed to change status. Please try again.')
                        })
                      }
                    }}
                    style={{
                      padding: '8px 12px', display: 'flex', alignItems: 'center', gap: '9px',
                      cursor: 'pointer', fontSize: '12px',
                      background: agentStatus === opt.key ? '#F8FAFB' : 'transparent',
                    }}
                    onMouseEnter={e => e.currentTarget.style.background = '#F8FAFB'}
                    onMouseLeave={e => e.currentTarget.style.background = agentStatus === opt.key ? '#F8FAFB' : 'transparent'}
                  >
                    <span style={{ width: '8px', height: '8px', borderRadius: '50%', background: opt.color, flexShrink: 0 }} />
                    <span style={{ flex: 1 }}>{opt.label}</span>
                    <span style={{ fontSize: '10px', color: '#9AA5B1' }}>{opt.sub}</span>
                  </div>
                ))}
              </div>
            )}
            {statusError && (
              <div style={{
                position: 'absolute', top: 'calc(100% + 8px)', right: 0, zIndex: 60,
                background: '#FEF2F2', border: '1px solid #FECACA', borderLeft: '3px solid #EF4444',
                borderRadius: 7, padding: '8px 12px', fontSize: 12, color: '#991B1B',
                fontWeight: 500, whiteSpace: 'nowrap', boxShadow: '0 4px 12px rgba(0,0,0,.1)',
              }}>
                {statusError}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* KPI strip — 6 cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5,1fr)', gap: '10px', marginBottom: '14px' }}>
        {kpis.map((k, i) => (
          <div key={i} style={{
            background: '#fff', border: '1px solid #E0E7EF', borderRadius: '8px',
            padding: '10px 8px', borderLeft: `4px solid ${k.color}`,
            position: 'relative', overflow: 'hidden',
          }}>
            <div style={{ fontSize: '9px', textTransform: 'uppercase', letterSpacing: '.5px', color: '#5A6672', fontWeight: 600, marginBottom: '3px' }}>{k.label}</div>
            <div style={{ fontSize: '22px', fontWeight: 700, color: '#2B2B2B', fontFamily: "'Courier New',monospace" }}>{loading ? '—' : k.value}</div>
            <div style={{ fontSize: '10px', color: k.sub === 'Live' ? '#E53935' : '#5A6672', fontWeight: k.sub === 'Live' ? 600 : 400, marginTop: '2px' }}>{k.sub}</div>
          </div>
        ))}
      </div>

      {/* 3-card grid */}
      <div style={{ display: 'grid', gridTemplateColumns: '1.4fr 1fr 1fr', gap: '12px' }}>

        {/* Card 1: Top Performing Agents */}
        <div style={{ background: '#fff', border: '1px solid #E0E7EF', borderRadius: '8px', padding: '14px' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '10px' }}>
            <h3 style={{ fontSize: '12px', fontWeight: 600, color: '#2B2B2B', margin: 0 }}>Top Performing Agents</h3>
            <span style={{ fontSize: '9px', color: '#9AA5B1' }}>today</span>
          </div>
          {loading ? (
            <div style={{ fontSize: '12px', color: '#9AA5B1', padding: '8px 0' }}>Loading…</div>
          ) : topAgents.length === 0 ? (
            <div style={{ fontSize: '12px', color: '#9AA5B1', padding: '8px 0' }}>No data yet.</div>
          ) : topAgents.map((ag, i) => {
            const c = colorForStatus(ag.status)
            return (
              <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '9px', padding: '8px 0', borderBottom: '1px solid #E0E7EF', cursor: 'pointer' }}
                onMouseEnter={e => e.currentTarget.style.background = '#F8FAFB'}
                onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
              >
                <div style={{ width: '30px', height: '30px', borderRadius: '50%', background: c, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontSize: '11px', fontWeight: 700, flexShrink: 0, position: 'relative' }}>
                  {initials(ag.agent_name).slice(0, 1)}
                  <div style={{ position: 'absolute', bottom: '-1px', right: '-1px', width: '9px', height: '9px', borderRadius: '50%', background: c, border: '2px solid #fff' }} />
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: '12px', fontWeight: 500, color: '#2B2B2B' }}>{ag.agent_name}</div>
                  <div style={{ fontSize: '10px', color: '#5A6672' }}>{ag.agent_code || 'Agent'}</div>
                </div>
                <div>
                  <div style={{ fontSize: '13px', fontWeight: 700, color: '#0090D9', fontFamily: "'Courier New',monospace", textAlign: 'right' }}>{ag.call_count ?? 0}</div>
                  <div style={{ fontSize: '9px', color: '#9AA5B1', textTransform: 'uppercase', letterSpacing: '.3px' }}>calls</div>
                </div>
              </div>
            )
          })}
        </div>

        {/* Card 2: Interactions Status + Shift Hours Bars */}
        <div style={{ background: '#fff', border: '1px solid #E0E7EF', borderRadius: '8px', padding: '14px' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '10px' }}>
            <h3 style={{ fontSize: '12px', fontWeight: 600, color: '#2B2B2B', margin: 0 }}>Interactions Status</h3>
            <span style={{ fontSize: '9px', color: '#9AA5B1' }}>shift total</span>
          </div>
          <div style={{ display: 'flex', gap: '14px', alignItems: 'center' }}>
            <div style={{ width: '100px', height: '100px', position: 'relative', flexShrink: 0 }}>
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie data={donutData} cx="50%" cy="50%" innerRadius={30} outerRadius={46}
                    dataKey="value" startAngle={90} endAngle={-270} strokeWidth={0}>
                    {donutData.map((d, i) => <Cell key={i} fill={d.color} />)}
                  </Pie>
                </PieChart>
              </ResponsiveContainer>
              <div style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%,-50%)', textAlign: 'center' }}>
                <div style={{ fontSize: '18px', fontWeight: 700, color: '#2B2B2B', lineHeight: 1, fontFamily: "'Courier New',monospace" }}>{connected + failed + abandoned}</div>
                <div style={{ fontSize: '8px', color: '#9AA5B1', textTransform: 'uppercase', letterSpacing: '.3px' }}>total</div>
              </div>
            </div>
            <div style={{ flex: 1 }}>
              {donutData.map((d, i) => (
                <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '4px 0', fontSize: '11px', color: '#5A6672' }}>
                  <span style={{ width: '9px', height: '9px', borderRadius: '2px', background: d.color, flexShrink: 0 }} />
                  <span style={{ flex: 1 }}>{d.name}</span>
                  <span style={{ fontWeight: 700, fontFamily: "'Courier New',monospace", color: '#2B2B2B' }}>{d.value}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Shift hours progress bars */}
          <div style={{ marginTop: '14px', borderTop: '1px solid #E0E7EF', paddingTop: '12px', display: 'flex', flexDirection: 'column', gap: '10px' }}>
            {[
              { label: 'Shift Hours Done', done: fmtHMS(shiftHoursSecs), target: fmtHMS(shiftTargetSecs), pct: shiftPct, grad: '#56A0D3,#89C4EE', track: '#E5EAF2', dot: '#56A0D3', note: `${shiftPct}% of shift completed` },
              { label: 'Break Hours',      done: fmtHMS(breakSecs),      target: fmtHMS(breakTargetSecs), pct: breakPct, grad: '#F07878,#F5A5A5', track: '#FEE2E2', dot: '#F07878', note: `${fmtHMS(Math.max(0, breakTargetSecs - breakSecs))} remaining` },
              { label: 'Working Hours',    done: fmtHMS(workSecs),        target: fmtHMS(Math.max(0, shiftTargetSecs - breakTargetSecs)), pct: workPct, grad: '#3EC97B,#7DDFA5', track: '#DCFCE7', dot: '#3EC97B', note: `${workPct}% productive time` },
            ].map((bar, i) => (
              <div key={i}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '4px' }}>
                  <span style={{ fontSize: '11px', fontWeight: 600, color: '#5A6672' }}>{bar.label}</span>
                  <span style={{ fontSize: '11px', fontWeight: 700, fontFamily: "'Courier New',monospace", color: '#2B2B2B' }}>
                    {bar.done} <span style={{ fontWeight: 400, color: '#9AA5B1' }}>/ {bar.target}</span>
                  </span>
                </div>
                <div style={{ background: bar.track, borderRadius: '99px', height: '8px', overflow: 'hidden' }}>
                  <div style={{ width: `${bar.pct}%`, height: '100%', background: `linear-gradient(90deg,${bar.grad})`, borderRadius: '99px', position: 'relative', minWidth: bar.pct > 0 ? '12px' : '0' }}>
                    {bar.pct > 0 && <div style={{ position: 'absolute', right: 0, top: '50%', transform: 'translate(50%,-50%)', width: '12px', height: '12px', borderRadius: '50%', background: bar.dot, border: '2px solid #fff', boxShadow: '0 1px 3px rgba(0,0,0,.2)' }} />}
                  </div>
                </div>
                <div style={{ fontSize: '10px', color: '#9AA5B1', marginTop: '3px' }}>{bar.note}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Card 3: Shift Breakdown */}
        <div style={{ background: '#fff', border: '1px solid #E0E7EF', borderRadius: '8px', padding: '14px', display: 'flex', flexDirection: 'column' }}>
          <div style={{ marginBottom: '12px' }}>
            <h3 style={{ fontSize: '12px', fontWeight: 600, color: '#2B2B2B', margin: 0 }}>Shift Breakdown</h3>
          </div>
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
            {breakdownArr.length === 0 ? (
              <div style={{ fontSize: '13px', color: '#9AA5B1', textAlign: 'center', padding: '12px 0' }}>No data</div>
            ) : breakdownArr.map((item, i) => (
              <div key={i} style={{
                display: 'flex', alignItems: 'center', gap: '8px',
                padding: '6px 0', fontSize: '13px', color: '#5A6672',
                borderBottom: i < breakdownArr.length - 1 ? '1px solid #F0F4F8' : 'none',
              }}>
                <span style={{ width: '11px', height: '11px', borderRadius: '3px', background: BREAKDOWN_COLORS[item.status] || '#9AA5B4', flexShrink: 0 }} />
                <span style={{ flex: 1 }}>{BREAKDOWN_LABELS[item.status] || item.status}</span>
                <span style={{ fontWeight: 700, fontFamily: "'Courier New',monospace", color: '#2B2B2B', fontSize: '13px' }}>
                  {item.display || (item.minutes === 0 ? '—' : `${item.minutes} mins`)}
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  )
}

export default AgentWorkspacePage
